import { useState, useEffect, useCallback } from 'react'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DateRangeModal } from '@/components/ui/DateRangeModal'
import { MobileFloatingFilter } from '@/components/ui/MobileFloatingFilter'
import {
  XCircle,
  AlertTriangle,
  Search,
  RefreshCw,
  Loader2,
  Clock,
  User,
  CheckCircle,
  Hash,
  Filter,
} from 'lucide-react'
import { ApiService } from '@/services/api'
import { parseDate, createDateRange } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'
import { useCurrency } from '@/hooks/useCurrency'

interface CancelledItem {
  name: string
  quantity: number
  price: number
}

interface CancellationEntry {
  _id: string
  cancelledBy: string
  cancelReason: string
  tableNumber: string
  orderType: string
  kotNumber: string
  items: CancelledItem[]
  date: string
  createdAt: string
}

interface UserBreakdown {
  cancelledBy: string
  count: number
  lostRevenue: number
}

interface OrderTypeBreakdown {
  type: string
  count: number
  lostRevenue: number
}

interface ItemBreakdown {
  name: string
  quantity: number
  occurrences: number
  lostRevenue: number
}

interface ReasonBreakdown {
  reason: string
  count: number
}

interface CancellationReportData {
  summary: { totalCancellations: number; totalLostRevenue: number }
  cancellations: CancellationEntry[]
  byUser: UserBreakdown[]
  byOrderType: OrderTypeBreakdown[]
  byItem: ItemBreakdown[]
  byReason: ReasonBreakdown[]
}

function formatDisplayDate(dateVal: string | Date | undefined): string {
  if (!dateVal) return '-'
  try {
    return new Date(dateVal).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  } catch {
    return String(dateVal)
  }
}

function getOrderTypeLabel(orderType: any): string {
  if (!orderType) return 'Dine-in'
  if (typeof orderType === 'object') return orderType?.category || 'Dine-in'
  return String(orderType)
}

export default function CancellationsScreen() {
  const { formatCurrency } = useCurrency()
  const { currentRestaurant } = useRestaurant()
  const [reportData, setReportData] = useState<CancellationReportData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()

  const loadReport = useCallback(async () => {
    if (!currentRestaurant?._id) return
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.getCancellationReport(currentRestaurant._id, dateRange)
      if (response.success) setReportData(response.data)
      else setError(response.message || 'Failed to load cancellation report')
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as any).response?.data?.message || 'Failed to load cancellation report'
        : 'Failed to load cancellation report'
      setError(msg)
    } finally {
      setIsLoading(false)
    }
  }, [currentRestaurant?._id, dateRange])

  useEffect(() => {
    if (currentRestaurant?._id) loadReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRestaurant?._id, dateRange])

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    const newDate = parseDate(value)
    setDateRange(prev =>
      field === 'from' ? createDateRange(newDate, prev.to) : createDateRange(prev.from, newDate)
    )
  }

  const { summary, cancellations, byUser, byOrderType, byItem, byReason } = reportData ?? {
    summary: { totalCancellations: 0, totalLostRevenue: 0 },
    cancellations: [],
    byUser: [],
    byOrderType: [],
    byItem: [],
    byReason: []
  }

  const totalCancellations = summary.totalCancellations

  const filtered = cancellations.filter(c =>
    !searchTerm ||
    c.cancelledBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.tableNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.cancelReason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.items?.some(i => i.name?.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2 text-muted-foreground">Loading cancellations...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-destructive/10">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
        <div className="text-center space-y-1">
          <p className="font-semibold text-destructive">Failed to load cancellations</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
        <Button variant="outline" onClick={loadReport} disabled={isLoading}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Cancellations</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Who cancelled which items, and when</p>
        </div>
        <Button onClick={loadReport} variant="outline" size="icon" disabled={isLoading}
          className="py-[10px] sm:w-auto sm:h-auto sm:px-4 flex-shrink-0">
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Date Range and Search - Desktop */}
      <Card className="hidden md:block">
        <CardHeader>
          <CardTitle className="text-base flex items-center space-x-2">
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:gap-4 grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2 lg:col-span-2">
              <Label>Date Range</Label>
              <DateRangeModal
                dateRange={dateRange}
                onDateChange={handleDateChange}
                onApply={loadReport}
                title="Cancellation Report Date Range"
                description="Select the date range for your cancellation report"
              />
            </div>
            <div className="space-y-2 lg:col-span-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search captain, table, item, reason..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search - Mobile */}
      <div className="relative md:hidden">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search captain, table, item, reason..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <MobileFloatingFilter
        dateRange={dateRange}
        onDateChange={handleDateChange}
        onApply={loadReport}
        title="Cancellation Report Date Range"
        description="Select the date range for your cancellation report"
      />

      {/* Summary Cards */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Cancellations</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{totalCancellations}</div>
            <p className="text-xs text-muted-foreground mt-1">Cancelled KOTs</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Captains Involved</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{byUser.length}</div>
            <p className="text-xs text-muted-foreground mt-1">Staff with cancellations</p>
          </CardContent>
        </Card>

        <Card className="col-span-2 lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Items Cancelled</CardTitle>
            <Hash className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {cancellations.reduce((sum, c) => sum + c.items.reduce((s, i) => s + (i.quantity || 1), 0), 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Individual item qty cancelled</p>
          </CardContent>
        </Card>
      </div>

      {/* No data */}
      {totalCancellations === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-medium">No cancellations found</p>
          </CardContent>
        </Card>
      )}

      {totalCancellations > 0 && (
        <>
          {/* By Captain */}
          {byUser.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <User className="h-4 w-4" />
                  By Captain
                </CardTitle>
                <CardDescription>Which staff cancelled the most orders</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Captain</TableHead>
                      <TableHead className="text-center">Cancellations</TableHead>
                      <TableHead className="text-center">% of Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byUser.map((u) => {
                      const pct = totalCancellations > 0 ? (u.count / totalCancellations) * 100 : 0
                      return (
                        <TableRow key={u.cancelledBy}>
                          <TableCell className="font-medium">{u.cancelledBy}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="destructive" className="text-xs">{u.count}</Badge>
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground text-sm">
                            {pct.toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Most Cancelled Items */}
          {byItem.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Hash className="h-4 w-4" />
                  Most Cancelled Items
                </CardTitle>
                <CardDescription>Which menu items get cancelled the most in the selected period</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center">Qty Cancelled</TableHead>
                      <TableHead className="text-center">Times</TableHead>
                      <TableHead className="text-right">Lost Revenue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byItem.slice(0, 10).map((item) => (
                      <TableRow key={item.name}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="destructive" className="text-xs">{item.quantity}</Badge>
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground text-sm">{item.occurrences}</TableCell>
                        <TableCell className="text-right text-muted-foreground text-sm">
                          {formatCurrency(item.lostRevenue)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Top Cancellation Reasons */}
          {byReason.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <AlertTriangle className="h-4 w-4" />
                  Top Cancellation Reasons
                </CardTitle>
                <CardDescription>Most common reasons given for cancelling</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reason</TableHead>
                      <TableHead className="text-center">Count</TableHead>
                      <TableHead className="text-center">% of Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byReason.slice(0, 10).map((r) => {
                      const pct = totalCancellations > 0 ? (r.count / totalCancellations) * 100 : 0
                      return (
                        <TableRow key={r.reason}>
                          <TableCell className="font-medium">{r.reason}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="destructive" className="text-xs">{r.count}</Badge>
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground text-sm">
                            {pct.toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Cancellations Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <XCircle className="h-4 w-4" />
                Cancellations
              </CardTitle>
              <CardDescription>
                {filtered.length === cancellations.length
                  ? `${filtered.length} record${filtered.length !== 1 ? 's' : ''}`
                  : `${filtered.length} of ${cancellations.length} shown`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Desktop Table */}
              <div className="hidden lg:block">
                <div className="max-h-[600px] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background">
                      <TableRow>
                        <TableHead>KOT#</TableHead>
                        <TableHead>Captain</TableHead>
                        <TableHead>Table</TableHead>
                        <TableHead>Order Type</TableHead>
                        <TableHead>Cancelled Items</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Date & Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                            No results match your search
                          </TableCell>
                        </TableRow>
                      ) : (
                        filtered.map((c) => (
                          <TableRow key={c._id}>
                            <TableCell className="text-xs text-muted-foreground font-mono">
                              <div>{c.kotNumber || '-'}</div>
                              <div className="text-[10px] opacity-50 select-all">{c._id.slice(-8)}</div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                <User className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                                <span className="font-medium">{c.cancelledBy}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{c.tableNumber}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize text-xs">
                                {getOrderTypeLabel(c.orderType)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-0.5 max-w-[240px]">
                                {c.items.map((item, idx) => (
                                  <div key={idx} className="text-sm">
                                    {item.name}
                                    <span className="text-muted-foreground ml-1.5">×{item.quantity}</span>
                                  </div>
                                ))}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground max-w-[160px]">
                              {c.cancelReason || '-'}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3 flex-shrink-0" />
                                {formatDisplayDate(c.date)}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-3">
                {filtered.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground">No results match your search</div>
                ) : (
                  filtered.map((c) => (
                    <div key={c._id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-semibold">{c.cancelledBy}</span>
                        </div>
                        <Badge variant="destructive" className="text-xs">Cancelled</Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                        <div>
                          <div className="text-xs text-muted-foreground">Table</div>
                          <div className="font-medium">{c.tableNumber}</div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Order Type</div>
                          <div className="font-medium capitalize">{getOrderTypeLabel(c.orderType)}</div>
                        </div>
                        {c.cancelReason && (
                          <div className="col-span-2">
                            <div className="text-xs text-muted-foreground">Reason</div>
                            <div className="text-sm">{c.cancelReason}</div>
                          </div>
                        )}
                      </div>

                      <div className="border-t pt-2 space-y-1">
                        <div className="text-xs text-muted-foreground font-medium mb-1">Cancelled Items</div>
                        {c.items.map((item, idx) => (
                          <div key={idx} className="text-sm">
                            {item.name} <span className="text-muted-foreground">×{item.quantity}</span>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center gap-1 text-xs text-muted-foreground pt-1 border-t">
                        <Clock className="h-3 w-3" />
                        {formatDisplayDate(c.date)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* By Order Type */}
          {byOrderType.length > 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Hash className="h-4 w-4" />
                  By Order Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
                  {byOrderType.map((t) => (
                    <div key={t.type} className="border rounded-lg p-3 text-center">
                      <div className="text-xs text-muted-foreground capitalize mb-1">{getOrderTypeLabel(t.type)}</div>
                      <div className="text-xl font-bold text-red-600">{t.count}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
